import React, { useContext } from 'react';

import { gql } from '@nerdwallet/apollo';
import { Box, Column, Columns, Type } from '@nerdwallet/currency';
import { Impression } from '@nerdwallet/analytics/react/impression';
import { ExperimentContext } from '@nerdwallet/features';

import {
  GraphQlMarketplaceStarRating,
  LAYOUTS,
} from '@nerdwallet/react-marketplace-star-rating';
import MarketplaceCta, { LINK_TYPES } from '@nerdwallet/react-marketplace-cta';
import { APPLY_NOW_LINK_TYPES } from '@nerdwallet/react-marketplace-apply-now';

import styles from './cc-head-to-head.module.less';

const ENTITY_NAME = 'shopping_product_card';
const SECTION_NAME = 'cc_article_header';

const CardBox = ({ marketplaceEntity }) => {
  const {
    termsAndFeesLink,
    image: { alt, sourceMedium },
  } = marketplaceEntity;

  const image = (
    <MarketplaceCta
      analyticsPayloadOverrides={{
        section_name: SECTION_NAME,
      }}
      gqlMarketplaceEntity={marketplaceEntity}
      linkType={LINK_TYPES.productImage}
    >
      <img className={styles.image} src={sourceMedium} alt={alt} />
    </MarketplaceCta>
  );

  const name = (
    <Type className={styles.name} bold size="2" color="neutral-darkest">
      {marketplaceEntity.displayName}
    </Type>
  );

  const starRating = (
    <GraphQlMarketplaceStarRating
      className={styles.starRating}
      omitRatingNumberHref={false}
      omitRatingTextHref
      layout={LAYOUTS.SMALL_NUMERIC_INLINE}
      marketplaceEntity={marketplaceEntity}
    />
  );

  const applyNow = (
    <MarketplaceCta
      analyticsPayloadOverrides={{
        section_name: SECTION_NAME,
      }}
      gqlMarketplaceEntity={marketplaceEntity}
    />
  );

  const termsAndFees = termsAndFeesLink && (
    <Box className={styles.termsAndFees}>
      <MarketplaceCta
        analyticsPayloadOverrides={{
          section_name: SECTION_NAME,
        }}
        cta={{
          link: termsAndFeesLink,
          text: 'Rates & Fees',
          // although this is a rates and fees shopping finished event we want the
          // component to be an ApplyNowLink so that it's a link with the tracking
          type: 'APPLY_NOW',
        }}
        linkType={APPLY_NOW_LINK_TYPES.ratesAndFeesText}
        gqlMarketplaceEntity={marketplaceEntity}
        omitImpression
      />
    </Box>
  );

  const experimentContext = useContext(ExperimentContext);
  const modelExecutionIds = experimentContext?.modelExecutionIds;

  const {
    productType,
    monetizable,
    analyticsProps: { institutionId, offerId, productId },
    cta: { link },
  } = marketplaceEntity;

  return (
    <Impression
      eventType="product_impression"
      payload={{
        entity_name: ENTITY_NAME,
        section_name: SECTION_NAME,
        model_execution_ids: modelExecutionIds,
        destination_url: link,
        marketplace_institution_id: institutionId,
        marketplace_offer_id: offerId,
        marketplace_product_id: productId,
        marketplace_product_type: productType,
        monetizable,
      }}
    >
      <Box className={styles.cardBox}>
        <Box className={styles.info}>
          <Columns>
            <Column width="4">{image}</Column>
            <Column width="8">
              <Box
                className={styles.text}
                display="flex"
                flexDirection="column"
              >
                {name}
                {starRating}
              </Box>
            </Column>
          </Columns>
        </Box>
        <Box className={styles.ctas}>
          {applyNow}
          {termsAndFees}
        </Box>
      </Box>
    </Impression>
  );
};

const CCHeadToHead = ({ article }) => {
  const { creditCardsHeadToHead: marketplaceEntities } = article;

  if (!marketplaceEntities) {
    return null;
  }

  return (
    <Box className={styles.ccHeadToHead}>
      {marketplaceEntities.map((marketplaceEntity) => {
        return <CardBox marketplaceEntity={marketplaceEntity} />;
      })}
    </Box>
  );
};

CCHeadToHead.fragment = gql`
  fragment CCHeadToHead on Article {
    creditCardsHeadToHead {
      productType
      ... on CreditCardsOfferV2 {
        displayName
        starRating
        productType
        phoneNumber
        reviewLink
        image {
          sourceMedium
          alt
        }
        termsAndFeesLink
      }

      ... on MarketplaceEntityWithPlacementInterface {
        analyticsProps {
          hasLegacyImpression
          legacyAdditionalEventData
          legacyIssuer
          legacySlug
          legacyVertical
          institutionId
          offerId
          productId
        }
        cta {
          link
          subtext
          text
          type
        }
        monetizable
      }
    }
  }
`;

export default CCHeadToHead;
